/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/utils/aiModels.ts
 */

import type { SupportedModels } from '@repo/common/types/Exchange';

export interface AIModel {
  id: SupportedModels;
  name: string;
  shortName: string;
  description: string;
  provider: 'openai' | 'anthropic' | 'google' | 'meta';
  userSelectable?: boolean;
  premium?: boolean;
}

export const aiModels: AIModel[] = [
  {
    id: 'meta-llama/llama-4-scout-17b-16e-instruct',
    name: 'Llama 4 Scout 17B',
    shortName: 'Llama Scout',
    description: 'Fastest and most cost effective model',
    provider: 'meta',
    userSelectable: false // Private model for internal use
  },
  {
    id: 'gpt-5.2',
    name: 'GPT-5.2',
    shortName: '5.2',
    description: 'Latest powerful model',
    provider: 'openai',
    userSelectable: true
  },

  {
    id: 'gpt-5.2-mini',
    name: 'GPT-5.2 Mini',
    shortName: '5.2 Mini',
    description: 'Latest balanced model',
    provider: 'openai',
    userSelectable: true
  },

  {
    id: 'gpt-5.2-nano',
    name: 'GPT-5.2 Nano',
    shortName: '5.2 Nano',
    description: 'Latest fastest model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'gpt-5',
    name: 'GPT-5 (Legacy)',
    shortName: '5',
    description: 'Previous powerful model',
    provider: 'openai',
    userSelectable: false
  },
  {
    id: 'gpt-5-mini',
    name: 'GPT-5 Mini (Legacy)',
    shortName: '5 Mini',
    description: 'Previous balanced model',
    provider: 'openai',
    userSelectable: false
  },
  {
    id: 'gpt-5-nano',
    name: 'GPT-5 Nano (Legacy)',
    shortName: '5 Nano',
    description: 'Previous fastest model',
    provider: 'openai',
    userSelectable: false
  },
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    shortName: '4.1',
    description: 'Recent powerful model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'gpt-4.1-mini',
    name: 'GPT-4.1 Mini',
    shortName: '4.1 Mini',
    description: 'Recent balanced model',
    provider: 'openai',
    userSelectable: true
  },

  {
    id: 'gpt-4.1-nano',
    name: 'GPT-4.1 Nano',
    shortName: '4.1 Nano',
    description: 'Recent fastest model',
    provider: 'openai',
    userSelectable: true
  },

  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    shortName: '4o',
    description: 'Recent powerful model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    shortName: '4o Mini',
    description: 'Recent fast model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'openai/gpt-oss-120b',
    name: 'GPT-OSS 120B',
    shortName: 'GPT-OSS',
    description: 'Powerful open source model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'openai/gpt-oss-20b',
    name: 'GPT-OSS 20B',
    shortName: 'GPT-OSS',
    description: 'Fast open source model',
    provider: 'openai',
    userSelectable: true
  },
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    shortName: 'Sonnet 4.5',
    description: 'Latest model, best performance',
    provider: 'anthropic',
    userSelectable: true,
    premium: true
  },
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Claude Haiku 4.5',
    shortName: 'Haiku 4.5',
    description: 'Fastest model',
    provider: 'anthropic',
    userSelectable: true
  },
  {
    id: 'claude-3-5-sonnet-latest',
    name: 'Claude 3.5 Sonnet (Legacy)',
    shortName: 'Claude 3.5',
    description: 'Previous balanced model',
    provider: 'anthropic',
    userSelectable: false
  },
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Claude Sonnet 4 (Legacy)',
    shortName: 'Claude 4',
    description: 'Previous model',
    provider: 'anthropic',
    userSelectable: false
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro (Legacy)',
    shortName: 'Gemini 1.5 Pro',
    description: 'Previous high quality model',
    provider: 'google',
    userSelectable: false
  },
  {
    id: 'gemini-1.5-flash',
    name: 'Gemini 1.5 Flash (Legacy)',
    shortName: 'Gemini 1.5 Flash',
    description: 'Previous fast model',
    provider: 'google',
    userSelectable: false
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash (Legacy)',
    shortName: 'Gemini 2.5 Flash',
    description: 'Previous generation flash',
    provider: 'google',
    userSelectable: false
  },
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro (Legacy)',
    shortName: 'Gemini 2.5 Pro',
    description: 'Previous generation pro',
    provider: 'google',
    userSelectable: false
  },
  {
    id: 'gemini-3-flash-preview',
    name: 'Gemini 3 Flash Preview',
    shortName: 'G3 Flash',
    description: 'Next generation fast model',
    provider: 'google',
    userSelectable: true
  },
  {
    id: 'gemini-3-pro-preview',
    name: 'Gemini 3 Pro Preview',
    shortName: 'G3 Pro',
    description: 'Next generation powerful model',
    provider: 'google',
    userSelectable: true,
    premium: true
  }
];

// Get all model IDs for proxy credentials
export const getAllModelIds = (): string[] => {
  return aiModels.map((model) => model.id);
};

// Get models by provider
export const getModelsByProvider = (
  provider: 'openai' | 'anthropic' | 'google' | 'meta'
): AIModel[] => {
  return aiModels.filter((model) => model.provider === provider);
};

// Get model by ID
export const getModelById = (id: string): AIModel | undefined => {
  return aiModels.find((model) => model.id === id);
};

// Get user-selectable models for dropdowns
export const getUserSelectableModels = (): AIModel[] => {
  return aiModels.filter((model) => model.userSelectable !== false);
};

// Default model
export const DEFAULT_MODEL: SupportedModels = 'gpt-4.1';

// Check if user has premium access (simplified - in real app this would check subscription status)
export const hasPremiumAccess = (): boolean => {
  // This is a simplified check - in a real application, you would check the user's subscription status
  // For now, we'll return false to show premium models as locked
  return false;
};
